/* eslint-disable max-lines */
// deps
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

// internal-deps
import {
    CredEligibleData,
    PaymentMethodDowntime,
    TURBO_COD_CONFIRM_HALF_CARD_TYPE,
    TurboAutoPayMethod,
    TurboPaymentMethod,
    TurboPaymentSpecificActions,
    TurboPayOfferComms,
    TurboPodHalfCardEvents,
} from "@swiggy-private/payment-ui";
import { ApiResponseJuspayMeta, OnPayExtraParams, VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-core";
import {
    AnalyticsContext,
    NEWRELIC_TABLES,
    PlaceOrderData,
    recordNREvent,
    NOOP_OBJECT,
} from "@swiggy-private/checkout-core";
import { Storage } from "@swiggy-private/core-ui";

// hooks
import { useUPIHandler } from "@payment-client/components/show-payments/components/payment-group-handler/hooks/UseUPIHandler";
import { OnPay, useOnPay } from "@payment-client/hooks/UseOnPay";
import { useCardHandler } from "@payment-client/components/show-payments/components/payment-group-handler/hooks/UseCardHandler";
import { useSwiggyInstrumentsHandler } from "@payment-client/components/show-payments/components/payment-group-handler/hooks/UseSwiggyInstrumentsIHandler";
import { useWalletHandler } from "@payment-client/components/show-payments/components/payment-group-handler/hooks/UseWalletHandler";
import { usePayLaterHandler } from "@payment-client/components/show-payments/components/payment-group-handler/hooks/UsePayLaterHandler";
import { usePaymentActions } from "@payment-client/components/show-payments/components/payment-group-handler/hooks/UsePaymentActions";
import { useCredPayHandler } from "@payment-client/components/show-payments/components/payment-group-handler/hooks/UseCredPayHandler";

// context
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";
import { JuspayContext } from "@payment-client/contexts/JuspayContext";
import { PlaceOrderContext } from "@payment-client/contexts/PlaceOrderContext";
import { InAppUpiContext } from "@payment-client/contexts/InAppUpiContext";

//helpers
import { getTurboPaymentMethod } from "@payment-client/helpers/TurboPayment";
import { generatePaymentMethodId, getPaymentGroup } from "@payment-client/helpers/PaymentHelper";
import { getCODPaymentMethodForAutoPay } from "@payment-client/helpers/CodHelper";
import { isEmpty } from "@swiggy-private/js-utils";
import { GlobalHeaderState, SwgyHeaderKeysList } from "@swiggy-private/checkout-core";

// constants
import { PAYMENT_INITIAL_STATE } from "@payment-client/components/payment-main/PaymentMain";
import { SDK_TURBO_PLACE_ORDER_TIME } from "@payment-client/constants/StorageKeys";
import { TURBO_PLACE_ORDER_DEBUG_SUB_TYPE } from "@payment-client/components/turbo-place-order/TurboPlaceOrder";

//analytics
import {
    CartContextForAnalytics,
    onImpressionTurboAmount,
    onImpressionTurboPay,
    onImpressionTurboPayMsgs,
} from "@payment-client/analytics/events/TurboPayment";
import { SCREEN_NAME } from "@payment-client/analytics/constants";
import useCodHandler from "@payment-client/components/show-payments/components/payment-group-handler/hooks/useCodHandler";

interface UseTurboPaymentHandlerReturnType {
    turboPaymentMethod?: TurboPaymentMethod | null;
    loadingPayments: boolean;
    getTurboPaymentSpecificActions: (groupName: string) => TurboPaymentSpecificActions;
    paymentAmountWithoutDiscount: string;
    billTotal: string;
    juspayMeta?: ApiResponseJuspayMeta;
    showOverlay: boolean;
    credEligibleData: CredEligibleData;
    balanceValue?: string | number; // for wallets + swiggy-money
    turboPaymentOfferComms: TurboPayOfferComms;
    paymentMethodDowntime?: PaymentMethodDowntime;
    autoPaymethod: TurboAutoPayMethod;
    isFlipkartCoinsEligible: boolean;
    turboPodHalfCardEvents: TurboPodHalfCardEvents;
    amountWithDiscount: string;
    keyFactStatementUrl?: string;
    onKeyFactStatementClick?: (url: string) => void;
}

interface UseTurboPaymentHandlerOptions {
    isTurboV2?: boolean;
    billAmountShownOnCart?: string;
    uniqueId?: string;
    billAmountFromRenderingDetails?: string;
    cartContextForAnalytics?: CartContextForAnalytics;
}

export const useTurboPaymentHandler = (
    onError: () => void,
    onTurboPay?: (
        queryParams: string,
        extraParams?: OnPayExtraParams,
        extraArbitaryData?: PlaceOrderData["extraArbitaryData"],
    ) => void,
    options: UseTurboPaymentHandlerOptions = {},
): UseTurboPaymentHandlerReturnType => {
    const {
        isTurboV2 = false,
        billAmountShownOnCart,
        uniqueId,
        billAmountFromRenderingDetails,
        cartContextForAnalytics = NOOP_OBJECT,
    } = options;
    //using billTotal instead of amountToPay as splitpay is not being used
    const { billTotal } = useContext(PaymentPayContext);
    const { loadingPayments, paymentResponse } = useContext(ShowPaymentContext);
    const { placeOrderInProgress, processingOrder } = useContext(PlaceOrderContext);
    const { isSdkAvailableForInAppUpi } = useContext(InAppUpiContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const turboAmountMismatchRef = useRef<boolean>(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const previousUniqueIdRef = useRef<string | undefined>();
    const previousBillAmountShownOnCartRef = useRef<string | undefined>();
    const turboAmountImpressionRecordedRef = useRef<boolean>(false);
    const businessLine = GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine) ?? "";

    const onPayActual = useOnPay();

    const onPayForSDKFlow: OnPay = useCallback(
        (paymentMethod, extraParams, extraArbitaryData) => {
            if (typeof onTurboPay === "function") {
                const uniqueId = generatePaymentMethodId(paymentMethod);
                const searchParams = new URLSearchParams();
                searchParams.set("state", PAYMENT_INITIAL_STATE.TURBO_PLACE_ORDER);
                searchParams.set("selectedturbomethod", uniqueId);

                // recording turbo place action (swipe) timestamp for sdk turbo orders
                Storage.setItem(SDK_TURBO_PLACE_ORDER_TIME, String(new Date().getTime()));

                // TODO: remove after im turbo debug
                // sending event for place order
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TURBO_LOADER_DEBUG,
                    subType: TURBO_PLACE_ORDER_DEBUG_SUB_TYPE.PLACE_ORDER_CALL,
                });

                //This is to pass extra params and extra arbitrary data to SDK parent caller e.g.,
                // so that the same is passed to TurboPlaceOrder component
                onTurboPay(searchParams.toString(), extraParams, extraArbitaryData);
            }
        },
        [onTurboPay],
    );

    const onPay: OnPay = useMemo(() => {
        return typeof onTurboPay === "function" ? onPayForSDKFlow : onPayActual;
    }, [onPayActual, onPayForSDKFlow, onTurboPay]);

    const onPayWithCartAnalytics: OnPay = useCallback(
        (paymentMethod, extraParams, extraArbitaryData) => {
            if (isEmpty(cartContextForAnalytics)) {
                return onPay(paymentMethod, extraParams, extraArbitaryData);
            }
            return onPay(paymentMethod, extraParams, {
                ...extraArbitaryData,
                cartContextForAnalytics,
            });
        },
        [onPay, cartContextForAnalytics],
    );

    const {
        availableUpiIntentMethods,
        paymentActions: upiPaymentActions,
        upiIntentMethodFetchInProgress,
    } = useUPIHandler({
        onPay: onPayWithCartAnalytics,
        onSelect: () => void 0,
    });

    //Payment page screen name is being used in useCardHandler hook for analytics events
    //these event are not being used now but later we might have to change the screenName
    //as turbo payment is on cart page
    const genericPaymentActions = usePaymentActions({ onPay: onPayWithCartAnalytics, onSelect: () => void 0 });
    const { paymentActions: cardPaymentActions } = useCardHandler({
        onPay: onPayWithCartAnalytics,
        onSelect: () => void 0,
        paymentAmount: String(billTotal),
    });

    const { paymentActions: walletPaymentActions } = useWalletHandler({
        onPay: onPayWithCartAnalytics,
        onSelect: () => void 0,
        paymentAmount: String(billTotal),
    });
    const { paymentActions: swiggyInstrumentsPaymentActions } = useSwiggyInstrumentsHandler({
        onPay: onPayWithCartAnalytics,
        onSelect: () => void 0,
        paymentAmount: String(billTotal),
    });
    const { paymentActions: payLaterPaymentActions } = usePayLaterHandler({
        onPay: onPayWithCartAnalytics,
        onSelect: () => void 0,
        paymentAmount: String(billTotal),
    });

    const getTurboPaymentSpecificActions = useCallback(
        (groupName: string): TurboPaymentSpecificActions => {
            switch (groupName) {
                case VALID_PAYMENT_GROUP_TYPES.UPI:
                    return upiPaymentActions;

                case VALID_PAYMENT_GROUP_TYPES.FOOD_CARD:
                case VALID_PAYMENT_GROUP_TYPES.CARD:
                    return cardPaymentActions;

                case VALID_PAYMENT_GROUP_TYPES.WALLET_WEB:
                case VALID_PAYMENT_GROUP_TYPES.WALLET:
                case VALID_PAYMENT_GROUP_TYPES.BILL_TO_COMPANY:
                    return walletPaymentActions;

                case VALID_PAYMENT_GROUP_TYPES.SWIGGY_INSTRUMENTS:
                    return swiggyInstrumentsPaymentActions;

                case VALID_PAYMENT_GROUP_TYPES.PAY_LATER:
                    return payLaterPaymentActions;

                case VALID_PAYMENT_GROUP_TYPES.COD:
                case VALID_PAYMENT_GROUP_TYPES.CRED:
                case VALID_PAYMENT_GROUP_TYPES.INAPP_UPI:
                    return genericPaymentActions;

                default:
                    return undefined;
            }
        },
        [
            cardPaymentActions,
            genericPaymentActions,
            payLaterPaymentActions,
            swiggyInstrumentsPaymentActions,
            upiPaymentActions,
            walletPaymentActions,
        ],
    );

    //sdk available for CARD
    const { fetchingJuspaySdk } = useContext(JuspayContext);

    // cred eligibility data
    const credEligibleData = useCredPayHandler();

    const showTurboPayLoader =
        loadingPayments ||
        upiIntentMethodFetchInProgress === "notInitialted" ||
        upiIntentMethodFetchInProgress === true ||
        !!fetchingJuspaySdk;

    const paymentMethodInfo = useMemo(() => {
        if (showTurboPayLoader) {
            return { turboPaymentMethod: null };
        }

        const { hide, turboPaymentMethod, balanceValue, loading, keyFactStatementUrl } = getTurboPaymentMethod({
            availableUpiIntentMethods,
            paymentResponse,
            billTotal,
            isSdkAvailableForCred: credEligibleData.isSdkAvailable,
            isSdkAvailableForInAppUpi,
            getTurboPaymentSpecificActions,
        });

        if (hide) {
            onError();
        }

        return { turboPaymentMethod, balanceValue, loading, keyFactStatementUrl };
    }, [
        availableUpiIntentMethods,
        billTotal,
        credEligibleData.isSdkAvailable,
        getTurboPaymentSpecificActions,
        isSdkAvailableForInAppUpi,
        onError,
        paymentResponse,
        showTurboPayLoader,
    ]);

    const { turboPaymentMethod, balanceValue, loading, keyFactStatementUrl } = paymentMethodInfo;

    const autoPaymethod = useMemo(
        () => getCODPaymentMethodForAutoPay(paymentResponse?.paymentGroup),
        [paymentResponse?.paymentGroup],
    );

    const amountWithDiscount = useMemo(() => {
        return turboPaymentMethod?.payableAmount || String(billTotal);
    }, [turboPaymentMethod, billTotal]);

    const { codSpecificActions } = useCodHandler(SCREEN_NAME.CHECKOUT);

    const turboPodHalfCardEvents = useMemo(
        () => ({
            onLoad: (type: TURBO_COD_CONFIRM_HALF_CARD_TYPE) => {
                codSpecificActions?.onLoadCodHalfCard?.(type);
            },
            onPlaceOrderCtaClick: (flow: TURBO_COD_CONFIRM_HALF_CARD_TYPE, isSliderCompleted?: "yes" | "no") => {
                if (isSliderCompleted) {
                    codSpecificActions?.onPlaceCodHalfCardCTa?.(flow, isSliderCompleted);
                    return;
                }
                codSpecificActions?.onPlaceCodHalfCardCTa?.(flow);
            },
            onCancelCtaClick: (flow: TURBO_COD_CONFIRM_HALF_CARD_TYPE) => {
                codSpecificActions?.onCancelHalfCard?.(flow);
            },
        }),
        [codSpecificActions],
    );

    useEffect(() => {
        if (!paymentResponse && !loadingPayments) {
            onError();
        }
    }, [loadingPayments, onError, paymentResponse, turboPaymentMethod]);

    useEffect(() => {
        if (turboPaymentMethod) {
            onImpressionTurboPay({
                paymentCode: turboPaymentMethod.paymentCode,
                couponUsed: !!paymentResponse?.couponApplied,
                slider: !isTurboV2,
                impressionEvent,
                cartContextForAnalytics,
            });
            if (turboPaymentMethod.meta.promoMsg || turboPaymentMethod.discount) {
                onImpressionTurboPayMsgs({
                    impressionEvent,
                    billAmount: String(billTotal),
                    payableAmount: turboPaymentMethod?.payableAmount,
                    promoMsg: turboPaymentMethod.meta.promoMsg,
                    discountMsg: turboPaymentMethod.discount,
                    businessLine,
                });
            }
        }
    }, [impressionEvent, paymentResponse?.couponApplied, turboPaymentMethod, isTurboV2, cartContextForAnalytics]);

    useEffect(() => {
        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (isEmpty(billAmountShownOnCart) || isEmpty(amountWithDiscount) || showTurboPayLoader || loading) {
            return;
        }

        //if uniqueId is same as previous uniqueId, then return
        if (previousUniqueIdRef.current === uniqueId) {
            return;
        }

        previousUniqueIdRef.current = uniqueId;

        if (!turboAmountImpressionRecordedRef.current) {
            onImpressionTurboAmount({
                impressionEvent,
                billAmountShownOnCart,
                billTotal: String(billTotal),
                payableAmount: turboPaymentMethod?.payableAmount,
                billAmountFromRenderingDetails: billAmountFromRenderingDetails,
                uniqueId,
            });
            turboAmountImpressionRecordedRef.current = true;
        }

        // Reset flag if amounts match
        if (billAmountShownOnCart === amountWithDiscount) {
            turboAmountMismatchRef.current = false;
            return;
        }

        // Skip if already recorded
        if (turboAmountMismatchRef.current) {
            return;
        }

        if (previousBillAmountShownOnCartRef.current === amountWithDiscount) {
            recordNREvent({
                eventType: NEWRELIC_TABLES.TURBO_CART_AMOUNT_MISMATCH_V2,
                billAmountShownOnCart,
                turboAmount: amountWithDiscount,
                currentUniqueId: uniqueId || "",
                previousBillAmountShownOnCart: previousBillAmountShownOnCartRef.current || "",
                billTotal: String(billTotal),
                payableAmount: String(turboPaymentMethod?.payableAmount),
                billAmountFromRenderingDetails: billAmountFromRenderingDetails || "",
            });
            return;
        }

        previousBillAmountShownOnCartRef.current = billAmountShownOnCart;

        // Record timestamp for stability tracking
        timeoutRef.current = setTimeout(() => {
            if (billAmountShownOnCart !== amountWithDiscount && !turboAmountMismatchRef.current) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TURBO_CART_AMOUNT_MISMATCH,
                    billAmountShownOnCart,
                    turboAmount: amountWithDiscount,
                    currentUniqueId: uniqueId || "",
                    billTotal: String(billTotal),
                    payableAmount: String(turboPaymentMethod?.payableAmount),
                    billAmountFromRenderingDetails: billAmountFromRenderingDetails || "",
                });
                turboAmountMismatchRef.current = true;
            }
            timeoutRef.current = null;
        }, 3000); //3 seconds timeout, this event should be only triggered if the uniqueId is changed & when GPO is triggered to avoid false positives

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [
        billAmountShownOnCart,
        showTurboPayLoader,
        loading,
        amountWithDiscount,
        uniqueId,
        turboPaymentMethod,
        billTotal,
        billAmountFromRenderingDetails,
        impressionEvent,
    ]);

    const turboPaymentOfferComms: TurboPayOfferComms = {
        showTooltip: false,
        toolTipText: "",
    };

    if (paymentResponse) {
        const turboGroup = getPaymentGroup(paymentResponse, VALID_PAYMENT_GROUP_TYPES.SINGLE_CLICK);
        turboPaymentOfferComms.showTooltip = !!turboGroup?.paymentMethods?.[0]?.tooltip;
        turboPaymentOfferComms.toolTipText = turboGroup?.paymentMethods?.[0]?.tooltipText || "";
    }

    return {
        billTotal: String(billTotal),
        getTurboPaymentSpecificActions,
        loadingPayments: showTurboPayLoader || !!loading,
        paymentAmountWithoutDiscount: paymentResponse?.paymentAmountWithoutDiscount || "",
        turboPaymentMethod,
        juspayMeta: paymentResponse?.juspayMeta,
        showOverlay: placeOrderInProgress || processingOrder,
        balanceValue,
        credEligibleData,
        turboPaymentOfferComms,
        paymentMethodDowntime: paymentResponse?.paymentMethodDowntime,
        autoPaymethod,
        isFlipkartCoinsEligible: paymentResponse?.flipkartCoinsEligible || false,
        turboPodHalfCardEvents,
        amountWithDiscount,
        keyFactStatementUrl,
        onKeyFactStatementClick: payLaterPaymentActions.onKeyFactStatementClick,
    };
};
